import TitleBrowserAccordion from '@/components/OrderPlacement/OPStep2FileSelection/TitleBrowser/TitleBrowserAccordion';
import { Grid, Typography, GridProps } from '@mui/material';
import styled from '@emotion/styled';
import ListOverflowBadge from '@/components/_common/ListOverflowBadge/ListOverflowBadge';
import TitleVersionChip from '@/components/_common/TitleVersionChip';
import useCurrentBreakpoint from '@/components/_hooks/useCurrentBreakpoint';
import { CSSProperties, useMemo, useState } from 'react';
import { useConfigValue } from '@/components/_hooks/useConfigValue';
import { OrderPlacementConfig } from '@/__configs/config_types';
import ExpandedVersionRow from '@/components/OrderPlacement/OPStep2FileSelection/TitleBrowser/ExpandedVersionRow';
import { getAllVersionsAsArray, VersionHierarchyInput } from '@/_utils/bulkSearchUtils';

type BulkResultVersionEntityProps = {
  entity: VersionHierarchyInput;
  contrastNested?: boolean;
};

const BulkResultVersionEntity = ({ entity, contrastNested }: BulkResultVersionEntityProps) => {
  const checkEmbargoedVersions = useConfigValue<OrderPlacementConfig['checkEmbargoedVersions']>(['orderPlacementConfig', 'checkEmbargoedVersions']);
  const expandedVersionRows = useConfigValue<OrderPlacementConfig['expandedVersionRows']>(['orderPlacementConfig', 'expandedVersionRows']);
  const versions = useMemo(() => getAllVersionsAsArray(entity), [entity]);

  const entityVersion = versions[0];
  const [expanded, setExpanded] = useState(false);
  const currentBreakpoint = useCurrentBreakpoint();
  const headerOrientation = useMemo<{
    direction: CSSProperties['flexDirection'];
    justifyContent: CSSProperties['justifyContent'];
    alignItems: CSSProperties['alignItems'];
    xs: GridProps['xs'];
  }>(() => {
    return {
      direction: ['lg', 'xl'].includes(currentBreakpoint) ? 'row' : 'column',
      justifyContent: ['lg', 'xl'].includes(currentBreakpoint) ? 'space-between' : 'flex-start',
      alignItems: ['lg', 'xl'].includes(currentBreakpoint) ? 'center' : 'flex-start',
      xs: ['lg', 'xl'].includes(currentBreakpoint) ? 6 : 12
    };
  }, [currentBreakpoint]);

  if (!entityVersion) {
    return null;
  }

  const isEpisode = 'episode_sequence_order' in entityVersion.lineage_meta;

  return (
    <AccordionWrapped
      contrastNested={contrastNested}
      defaultExpanded={expanded}
      expandedCallback={setExpanded}
      title={
        <Grid
          container
          alignItems={headerOrientation.alignItems}
          justifyContent={headerOrientation.justifyContent}
          flexWrap="nowrap"
          flexDirection={headerOrientation.direction}
          sx={{ minHeight: 65 }}
        >
          <Grid item xs={headerOrientation.xs}>
            <Typography sx={{ fontSize: '1.1rem' }}>
              <span style={{ opacity: 0.6, paddingRight: 5 }}>
                {isEpisode ? (
                  `E${Number(entityVersion?.lineage_meta?.episode_sequence_order) || '?'}:`
                ) : (
                  `${entityVersion?.lineage_meta?.feature_name || '?'}`
                )}
              </span>
              {isEpisode ? entityVersion?.name : null}
              <Typography variant="caption" sx={{ ml: 1, whiteSpace: 'nowrap' }}>
                ID: {entityVersion?.external_id || entityVersion?.id}
              </Typography>
            </Typography>
          </Grid>
          <Grid item xs={headerOrientation.xs} sx={{ width: headerOrientation.xs === 12 ? '100%' : 'auto' }}>
            {!expanded && (
              <StyledActionGrid isFullRow={headerOrientation.xs === 12}>
                {versions.length > 0 ? (
                  <>
                    <StyledDiv>
                      <ListOverflowBadge
                        list={versions.map((version) => {
                          const isEmbargoed = checkEmbargoedVersions && version.is_embargoed;

                          return (
                            <TitleVersionChip
                              tooltip={isEmbargoed ? 'Embargoed cannot be selected' : undefined}
                              key={version.id}
                              version={version}
                              withTooltip
                              tooltipSource="global-channel-ids"
                            />
                          );
                        })}
                        delineated={false}
                      />
                    </StyledDiv>
                  </>
                ) : (
                  <Typography variant="caption">No versions</Typography>
                )}
              </StyledActionGrid>
            )}
          </Grid>
        </Grid>
      }
    >
      <Grid container>
        {expandedVersionRows ? (
          expandedVersionRows(versions, undefined, true, undefined, {
            canCreateEmbargoedTitleOrderPending: true,
            canCreateEmbargoedTitleOrderProgress: true,
          })
        ) : (
          <ExpandedVersionRow versions={versions} />
        )}
      </Grid>
    </AccordionWrapped>
  )
};

export default BulkResultVersionEntity;

const StyledActionGrid = styled(Grid, {
  shouldForwardProp: (prop: string) => !['isFullRow'].includes(prop)
})<{ isFullRow: boolean }>(({ theme, isFullRow }) => ({
  alignItems: 'center',
  justifyContent: isFullRow ? 'flex-start' : 'flex-end',
  display: 'flex',
  flexWrap: 'nowrap',
  paddingRight: theme.spacing(1),
  minHeight: '45px',

  '& > div': {
    marginLeft: 0,
    width: '100%',

    '& > div': {
      justifyContent: isFullRow ? 'flex-start' : 'flex-end'
    }
  }
}));

const StyledDiv = styled.div(() => ({
  overflow: 'hidden',
  alignItems: 'center',
  display: 'flex',
  flexWrap: 'nowrap',
  marginLeft: 'auto'
}));

const AccordionWrapped = styled(TitleBrowserAccordion)<{ contrastNested?: boolean }>`
  && > .MuiPaper-root.MuiAccordion-rounded {
    background: ${({ theme, contrastNested }) => contrastNested ? theme.palette.background.paper : undefined};
  }
`